import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  ArrowLeft,
  Plus, 
  Search, 
  Building2,
  Users,
  Loader2,
  Mail,
  Phone,
  Globe,
  FolderKanban,
  Pencil,
  DollarSign,
  Settings,
  Server,
  Eye,
  EyeOff,
  Save,
  CheckCircle2,
  HardDrive,
  Cloud,
  Image,
  Download,
  FolderOpen,
  FileArchive,
  Folder,
  ListTodo,
  Calendar as CalendarIcon,
  LogIn,
  UserCircle,
  Video,
  Clock,
  FileText,
  ExternalLink,
  Trash2,
  Briefcase,
  Hammer,
  Activity
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import MediaBrowser from '../components/MediaBrowser';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEET_BASE_URL = "https://meet.saysme.org";

const TenantDetail = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user, impersonateUser, impersonateClient } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [billingItems, setBillingItems] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditTenantOpen, setIsEditTenantOpen] = useState(false);
  const [isScheduleMeetingOpen, setIsScheduleMeetingOpen] = useState(false);
  const [isMeetingNotesOpen, setIsMeetingNotesOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [meetingDate, setMeetingDate] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    client_id: '',
    title: '',
    description: '',
    time: '10:00',
    duration_minutes: 30,
    send_email: true,
    invite_super_admin: false
  });
  const [tenantForm, setTenantForm] = useState({
    name: '',
    company_name: '',
    contact_email: '',
    phone: '',
    address: '',
    business_type: 'web_marketing',
    custom_domain: '',
    portal_slug: ''
  });
  const [clientForm, setClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    notes: ''
  });

  // SMTP Settings state
  const [isSmtpOpen, setIsSmtpOpen] = useState(false);
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [smtpExists, setSmtpExists] = useState(false);
  const [smtpSettings, setSmtpSettings] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: '',
    use_tls: true,
    is_active: true
  });

  // Storage Settings state
  const [isStorageOpen, setIsStorageOpen] = useState(false);
  const [storageLoading, setStorageLoading] = useState(false);
  const [showStorageSecret, setShowStorageSecret] = useState(false);
  const [storageSettings, setStorageSettings] = useState({
    endpoint: '',
    region: '',
    access_key_id: '',
    secret_access_key: '',
    bucket_name: '',
    is_active: true
  });

  // Media state
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [tenantMedia, setTenantMedia] = useState({ clients: [], total_files: 0, total_size: 0 });
  const [downloadingFolder, setDownloadingFolder] = useState(null);

  // Logo upload handler
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Please upload a PNG or JPEG image');
      return;
    }
    
    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error('Logo must be under 500KB');
      return;
    }
    
    setLogoUploading(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        try {
          await axios.post(`${API_URL}/tenants/${tenantId}/logo`, {
            logo: base64,
            content_type: file.type
          }, getAuthHeaders());
          toast.success('Logo uploaded successfully');
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to upload logo');
        } finally {
          setLogoUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process image');
      setLogoUploading(false);
    }
  };
  
  const handleDeleteLogo = async () => {
    if (!window.confirm('Remove tenant logo?')) return;
    try {
      await axios.delete(`${API_URL}/tenants/${tenantId}/logo`, getAuthHeaders());
      toast.success('Logo removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  useEffect(() => {
    fetchData();
    fetchSmtpSettings();
    fetchStorageSettings();
  }, [tenantId]);

  const fetchSmtpSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/smtp-settings/tenant/${tenantId}`, getAuthHeaders());
      if (res.data) {
        setSmtpSettings({ ...res.data, password: '' });
        setSmtpExists(true);
      }
    } catch (error) {
      // SMTP not configured yet - that's ok
    }
  };

  const fetchStorageSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/storage-settings/tenant/${tenantId}`, getAuthHeaders());
      if (res.data && res.data.endpoint) {
        setStorageSettings({
          endpoint: res.data.endpoint || '',
          region: res.data.region || '',
          access_key_id: res.data.access_key_id || '',
          secret_access_key: '',
          bucket_name: res.data.bucket_name || '',
          is_active: res.data.is_active ?? true
        });
      }
    } catch (error) {
      // Storage not configured yet
    }
  };

  const handleSaveStorage = async () => {
    if (!storageSettings.endpoint || !storageSettings.access_key_id || !storageSettings.bucket_name) {
      toast.error('Please fill in required storage fields');
      return;
    }

    setStorageLoading(true);
    try {
      await axios.post(`${API_URL}/storage-settings/tenant/${tenantId}`, storageSettings, getAuthHeaders());
      toast.success('Storage settings saved');
      setStorageSettings({ ...storageSettings, secret_access_key: '' });
      setIsStorageOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save storage settings');
    } finally {
      setStorageLoading(false);
    }
  };

  const fetchTenantMedia = async () => {
    setMediaLoading(true);
    try {
      const res = await axios.get(`${API_URL}/storage/tenant-media/${tenantId}`, getAuthHeaders());
      setTenantMedia(res.data);
    } catch (error) {
      setTenantMedia({ clients: [], total_files: 0, total_size: 0 });
    } finally {
      setMediaLoading(false);
    }
  };

  const handleDownloadClientFolder = async (clientPath, clientName) => {
    setDownloadingFolder(clientPath);
    try {
      const response = await axios.get(
        `${API_URL}/storage/download-folder?path=${encodeURIComponent(clientPath)}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${clientName}_files.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download folder');
    } finally {
      setDownloadingFolder(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleSaveSmtp = async () => {
    if (!smtpSettings.host || !smtpSettings.username || !smtpSettings.from_email) {
      toast.error('Please fill in required SMTP fields');
      return;
    }

    setSmtpLoading(true);
    try {
      if (smtpExists) {
        const updateData = { ...smtpSettings };
        if (!updateData.password) delete updateData.password;
        await axios.put(`${API_URL}/smtp-settings/tenant/${tenantId}`, updateData, getAuthHeaders());
        toast.success('SMTP settings updated');
      } else {
        if (!smtpSettings.password) {
          toast.error('Password is required for new SMTP configuration');
          setSmtpLoading(false);
          return;
        }
        await axios.post(`${API_URL}/smtp-settings/tenant/${tenantId}`, smtpSettings, getAuthHeaders());
        setSmtpExists(true);
        toast.success('SMTP settings created');
      }
      setSmtpSettings({ ...smtpSettings, password: '' });
      setIsSmtpOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMTP settings');
    } finally {
      setSmtpLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [tenantRes, clientsRes, projectsRes, invoicesRes, billingRes, tasksRes, usersRes, meetingsRes] = await Promise.all([
        axios.get(`${API_URL}/tenants/${tenantId}`, getAuthHeaders()),
        axios.get(`${API_URL}/clients?organization_id=${tenantId}`, getAuthHeaders()),
        axios.get(`${API_URL}/projects?organization_id=${tenantId}`, getAuthHeaders()),
        axios.get(`${API_URL}/invoices?organization_id=${tenantId}`, getAuthHeaders()),
        axios.get(`${API_URL}/billing-items?tenant_id=${tenantId}`, getAuthHeaders()),
        axios.get(`${API_URL}/tasks/upcoming?organization_id=${tenantId}&limit=5`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/users`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/meetings?organization_id=${tenantId}`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setTenant(tenantRes.data);
      setClients(clientsRes.data);
      setProjects(projectsRes.data);
      setInvoices(invoicesRes.data);
      setBillingItems(billingRes.data);
      setUpcomingTasks(tasksRes.data || []);
      setMeetings(meetingsRes.data || []);
      const allUsers = usersRes.data || [];
      setTenantUsers(allUsers.filter(u => u.organization_id === tenantId));
      setTenantForm({
        name: tenantRes.data.name,
        company_name: tenantRes.data.company_name || '',
        contact_email: tenantRes.data.contact_email || '',
        phone: tenantRes.data.phone || '',
        address: tenantRes.data.address || '',
        business_type: tenantRes.data.business_type || 'web_marketing',
        custom_domain: tenantRes.data.custom_domain || '',
        portal_slug: tenantRes.data.portal_slug || ''
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load tenant data');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-l-red-500';
      case 'high': return 'border-l-orange-500';
      case 'medium': return 'border-l-blue-500';
      default: return 'border-l-slate-300';
    }
  };

  const handleLoginAs = async (targetUser) => {
    if (!impersonateUser) {
      toast.error('Impersonation not available');
      return;
    }
    try {
      await impersonateUser(targetUser.id);
      toast.success(`Now logged in as ${targetUser.name}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to login as user');
    }
  };

  const handleLoginAsClient = async (client) => {
    try {
      const userData = await impersonateClient(client.id);
      toast.success(`Viewing portal as ${userData.name || client.name}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enter client portal');
    }
  };

  const handleScheduleMeeting = async (e) => {
    e.preventDefault();
    if (!meetingDate || !meetingForm.client_id) {
      toast.error('Please select a client and date');
      return;
    }
    try {
      const [hours, minutes] = meetingForm.time.split(':');
      const scheduledAt = new Date(meetingDate);
      scheduledAt.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      await axios.post(`${API_URL}/meetings`, {
        ...meetingForm,
        scheduled_at: scheduledAt.toISOString()
      }, getAuthHeaders());
      
      toast.success('Meeting scheduled successfully');
      setIsScheduleMeetingOpen(false);
      setMeetingForm({ client_id: '', title: '', description: '', time: '10:00', duration_minutes: 30, send_email: true, invite_super_admin: false });
      setMeetingDate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule meeting');
    }
  };

  const handleUpdateMeetingNotes = async () => {
    if (!selectedMeeting) return;
    try {
      await axios.put(`${API_URL}/meetings/${selectedMeeting.id}`, {
        notes: selectedMeeting.notes,
        status: selectedMeeting.status
      }, getAuthHeaders());
      toast.success('Meeting notes saved');
      setIsMeetingNotesOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Delete this meeting?')) return;
    try {
      await axios.delete(`${API_URL}/meetings/${meetingId}`, getAuthHeaders());
      toast.success('Meeting deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete meeting');
    }
  };

  const joinMeeting = (meeting) => {
    window.open(`${MEET_BASE_URL}/${meeting.room_name}`, '_blank');
  };

  const handleToggleTask = async (task) => {
    const newStatus = task.status === 'completed' ? 'todo' : 'completed';
    try {
      await axios.put(`${API_URL}/tasks/${task.id}`, { status: newStatus }, getAuthHeaders());
      // Refresh tasks
      const tasksRes = await axios.get(`${API_URL}/tasks/upcoming?organization_id=${tenantId}&limit=5`, getAuthHeaders());
      setUpcomingTasks(tasksRes.data || []);
    } catch (error) {
      console.error('Failed to update task');
    }
  };

  // Calculate balance due from unpaid invoices
  const unpaidInvoiceTotal = invoices
    .filter(inv => inv.status === 'sent' || inv.status === 'overdue' || inv.status === 'draft')
    .reduce((sum, inv) => sum + (inv.total || 0), 0);

  // Calculate yearly recurring total
  const yearlyRecurringTotal = billingItems
    .filter(item => item.is_active)
    .reduce((sum, item) => {
      if (item.billing_cycle === 'monthly') return sum + (item.amount * 12);
      if (item.billing_cycle === 'quarterly') return sum + (item.amount * 4);
      if (item.billing_cycle === 'yearly') return sum + item.amount;
      return sum;
    }, 0);

  // Total balance due = ONLY unpaid invoices (recurring is shown separately for reference)
  const totalBalanceDue = unpaidInvoiceTotal;

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/tenants/${tenantId}`, tenantForm, getAuthHeaders());
      toast.success('Tenant updated successfully');
      setIsEditTenantOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tenant');
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/clients`, {
        ...clientForm,
        organization_id: tenantId
      }, getAuthHeaders());
      toast.success('Client created successfully');
      setIsCreateOpen(false);
      setClientForm({ name: '', email: '', phone: '', company: '', website: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-slate-700">Tenant not found</h2>
        <Button variant="link" onClick={() => navigate('/tenants')}>Back to Tenants</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tenant-detail-page">
      {/* Back button and Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/tenants')}
          data-testid="back-to-tenants-btn"
        >
          <ArrowLeft size={20} />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            {tenant.company_name || tenant.name}
          </h1>
          <div className="flex items-center gap-3 mt-1">
            {tenant.company_name && tenant.name && (
              <p className="text-slate-500">Contact: {tenant.name}</p>
            )}
            {!tenant.company_name && <p className="text-slate-500">Tenant Dashboard</p>}
            {/* Business Type Badge */}
            <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${
              tenant.business_type === 'construction'
                ? 'bg-amber-50 text-amber-700'
                : tenant.business_type === 'dme_medical'
                ? 'bg-red-50 text-red-700'
                : 'bg-blue-50 text-blue-700'
            }`}>
              {tenant.business_type === 'construction' ? (
                <Hammer size={12} />
              ) : tenant.business_type === 'dme_medical' ? (
                <Activity size={12} />
              ) : (
                <Briefcase size={12} />
              )}
              {tenant.business_type === 'construction' ? 'Construction'
                : tenant.business_type === 'dme_medical' ? 'DME Medical'
                : 'Web & Marketing'}
            </span>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={() => setIsEditTenantOpen(true)}
          data-testid="edit-tenant-btn"
        >
          <Pencil size={16} className="mr-2" />
          Edit Tenant
        </Button>
        <Button 
          variant="outline" 
          onClick={() => setIsSmtpOpen(true)}
          data-testid="smtp-settings-btn"
        >
          <Settings size={16} className="mr-2" />
          Email Settings
        </Button>
        <Button 
          variant="outline" 
          onClick={() => setIsStorageOpen(true)}
          data-testid="storage-settings-btn"
        >
          <HardDrive size={16} className="mr-2" />
          Storage Settings
        </Button>
        <Button 
          variant="outline" 
          onClick={() => {
            setIsMediaOpen(true);
            fetchTenantMedia();
          }}
          data-testid="media-btn"
        >
          <Image size={16} className="mr-2" />
          Media
        </Button>
      </div>

      {/* Tenant Info Card */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            {/* Logo with upload */}
            <div className="relative group">
              {tenant.logo ? (
                <img 
                  src={`data:${tenant.logo_content_type || 'image/png'};base64,${tenant.logo}`}
                  alt={tenant.name}
                  className="w-16 h-16 rounded-xl object-contain border border-slate-200"
                  data-testid="tenant-logo-display"
                />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-slate-900 flex items-center justify-center">
                  <Building2 size={32} className="text-white" />
                </div>
              )}
              {/* Upload overlay */}
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <input 
                  type="file" 
                  accept="image/png,image/jpeg,image/jpg" 
                  className="hidden"
                  onChange={handleLogoUpload}
                  disabled={logoUploading}
                  data-testid="logo-upload-input"
                />
                {logoUploading ? (
                  <Loader2 size={20} className="text-white animate-spin" />
                ) : (
                  <span className="text-white text-xs font-medium">Upload</span>
                )}
              </label>
              {tenant.logo && (
                <button 
                  onClick={handleDeleteLogo}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  data-testid="delete-logo-btn"
                >
                  ×
                </button>
              )}
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-slate-500">Contact Email</p>
                <p className="font-medium text-slate-900">{tenant.contact_email || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Phone</p>
                <p className="font-medium text-slate-900">{tenant.phone || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Created</p>
                <p className="font-medium text-slate-900">{format(new Date(tenant.created_at), 'MMM d, yyyy')}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200 shadow-sm border-l-4 border-l-amber-500">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center">
                <DollarSign size={24} className="text-amber-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">${totalBalanceDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-sm text-slate-500">Total Balance Due</p>
                <span className="text-xs text-slate-400">Unpaid invoices</span>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{clients.length}</p>
                <p className="text-sm text-slate-500">Total Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center">
                <FolderKanban size={24} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{projects.length}</p>
                <p className="text-sm text-slate-500">Total Projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <DollarSign size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">${yearlyRecurringTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-sm text-slate-500">Yearly Recurring</p>
                <span className="text-xs text-blue-600">{billingItems.filter(i => i.is_active).length} active items</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Members Section */}
      {tenantUsers.length > 0 && user?.role === 'super_admin' && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Team Members
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tenantUsers.map((tenantUser) => (
              <Card key={tenantUser.id} className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <UserCircle size={24} className="text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{tenantUser.name}</p>
                        <p className="text-sm text-slate-500">{tenantUser.email}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          tenantUser.role === 'admin' ? 'bg-purple-100 text-purple-700' : 
                          tenantUser.role === 'team' ? 'bg-blue-100 text-blue-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {tenantUser.role}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLoginAs(tenantUser);
                      }}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      data-testid={`login-as-${tenantUser.id}`}
                    >
                      <LogIn size={14} className="mr-1" />
                      Login as
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Meetings Section */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            <Video size={20} className="inline mr-2 text-blue-600" />
            Meetings
          </h2>
          <Dialog open={isScheduleMeetingOpen} onOpenChange={setIsScheduleMeetingOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="schedule-meeting-btn">
                <Plus size={18} className="mr-2" />
                Schedule Meeting
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Schedule Meeting</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleScheduleMeeting} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <select
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    value={meetingForm.client_id}
                    onChange={(e) => setMeetingForm({ ...meetingForm, client_id: e.target.value })}
                    required
                    data-testid="meeting-client-select"
                  >
                    <option value="">Select a client...</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.company || c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Meeting Title *</Label>
                  <Input
                    value={meetingForm.title}
                    onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                    placeholder="e.g., Project Review"
                    required
                    data-testid="meeting-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={meetingForm.description}
                    onChange={(e) => setMeetingForm({ ...meetingForm, description: e.target.value })}
                    placeholder="Meeting agenda or notes..."
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {meetingDate ? format(meetingDate, 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={meetingDate} onSelect={setMeetingDate} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Time *</Label>
                    <Input
                      type="time"
                      value={meetingForm.time}
                      onChange={(e) => setMeetingForm({ ...meetingForm, time: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Duration (minutes)</Label>
                    <select
                      className="w-full px-3 py-2 border border-slate-200 rounded-md"
                      value={meetingForm.duration_minutes}
                      onChange={(e) => setMeetingForm({ ...meetingForm, duration_minutes: parseInt(e.target.value) })}
                    >
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>1 hour</option>
                      <option value={90}>1.5 hours</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </div>
                  <div className="space-y-2 flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={meetingForm.send_email}
                        onChange={(e) => setMeetingForm({ ...meetingForm, send_email: e.target.checked })}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm text-slate-600">Send email invite</span>
                    </label>
                  </div>
                </div>
                <div className="flex items-center gap-4 py-2 px-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={meetingForm.invite_super_admin}
                      onChange={(e) => setMeetingForm({ ...meetingForm, invite_super_admin: e.target.checked })}
                      className="rounded border-amber-400 text-amber-600"
                    />
                    <span className="text-sm text-amber-800 font-medium">Invite Mel Tallagsen - Software Engineer</span>
                  </label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsScheduleMeetingOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="create-meeting-btn">Schedule</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Meetings List */}
        {meetings.length === 0 ? (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-8 text-center">
              <Video size={40} className="mx-auto text-slate-300 mb-3" />
              <h3 className="text-lg font-medium text-slate-700">No meetings scheduled</h3>
              <p className="text-slate-500 mt-1">Schedule a meeting with your clients</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {meetings.slice(0, 6).map((meeting) => (
              <Card key={meeting.id} className={`border-slate-200 shadow-sm ${meeting.status === 'completed' ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base font-semibold text-slate-900">{meeting.title}</CardTitle>
                      <p className="text-sm text-slate-500">{meeting.client_company || meeting.client_name}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      meeting.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                      meeting.status === 'completed' ? 'bg-green-100 text-green-700' :
                      meeting.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {meeting.status}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="space-y-2 text-sm text-slate-600">
                    <p className="flex items-center gap-2">
                      <CalendarIcon size={14} className="text-slate-400" />
                      {format(new Date(meeting.scheduled_at), 'PPP')}
                    </p>
                    <p className="flex items-center gap-2">
                      <Clock size={14} className="text-slate-400" />
                      {format(new Date(meeting.scheduled_at), 'p')} ({meeting.duration_minutes} min)
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                    <Button
                      size="sm"
                      onClick={() => joinMeeting(meeting)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`join-meeting-${meeting.id}`}
                    >
                      <ExternalLink size={14} className="mr-1" />
                      Join
                    </Button>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setSelectedMeeting(meeting); setIsMeetingNotesOpen(true); }}
                        data-testid={`meeting-notes-${meeting.id}`}
                      >
                        <FileText size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteMeeting(meeting.id)}
                        data-testid={`delete-meeting-${meeting.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Meeting Notes Dialog */}
      <Dialog open={isMeetingNotesOpen} onOpenChange={setIsMeetingNotesOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <FileText size={18} className="inline mr-2" />
              Meeting Notes
            </DialogTitle>
          </DialogHeader>
          {selectedMeeting && (
            <div className="space-y-4 mt-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium text-slate-900">{selectedMeeting.title}</p>
                <p className="text-sm text-slate-500">{selectedMeeting.client_company || selectedMeeting.client_name}</p>
                <p className="text-sm text-slate-500">{format(new Date(selectedMeeting.scheduled_at), 'PPP p')}</p>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  value={selectedMeeting.status}
                  onChange={(e) => setSelectedMeeting({ ...selectedMeeting, status: e.target.value })}
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={selectedMeeting.notes || ''}
                  onChange={(e) => setSelectedMeeting({ ...selectedMeeting, notes: e.target.value })}
                  placeholder="Add meeting notes here..."
                  rows={6}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsMeetingNotesOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateMeetingNotes} className="bg-slate-900 hover:bg-slate-800">Save Notes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Clients Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Clients
        </h2>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-client-btn">
              <Plus size={18} className="mr-2" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Add New Client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClient} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Client Name *</Label>
                <Input
                  value={clientForm.name}
                  onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                  placeholder="e.g., John Smith"
                  required
                  data-testid="new-client-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={clientForm.email}
                    onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                    placeholder="client@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={clientForm.phone}
                    onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input
                    value={clientForm.company}
                    onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })}
                    placeholder="Company name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={clientForm.website}
                    onChange={(e) => setClientForm({ ...clientForm, website: e.target.value })}
                    placeholder="https://example.com"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={clientForm.notes}
                  onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-new-client-btn">
                  Add Client
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-tenant-clients-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Clients Grid */}
      {/* Clients and Tasks Grid - 3 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Clients - 2 columns */}
        <div className="lg:col-span-2">
          {filteredClients.length === 0 ? (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-12 text-center">
                <Users size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No clients yet</h3>
                <p className="text-slate-500 mt-1">Add your first client for this tenant</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredClients.map((client) => {
                // Find if there's a user account for this client
                const clientUser = tenantUsers.find(u => u.client_id === client.id);
                return (
                <Card 
                  key={client.id} 
                  className="border-slate-200 shadow-sm card-hover cursor-pointer"
                  onClick={() => navigate(`/clients/${client.id}`)}
                  data-testid={`tenant-client-card-${client.id}`}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold text-slate-900">
                      {client.name}
                    </CardTitle>
                    {client.company && (
                      <p className="text-sm text-slate-500">{client.company}</p>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {client.email && (
                        <p className="text-sm text-slate-600 flex items-center gap-2">
                          <Mail size={14} className="text-slate-400" />
                          {client.email}
                        </p>
                      )}
                      {client.phone && (
                        <p className="text-sm text-slate-600 flex items-center gap-2">
                          <Phone size={14} className="text-slate-400" />
                          {client.phone}
                        </p>
                      )}
                      {client.website && (
                        <p className="text-sm text-slate-600 flex items-center gap-2">
                          <Globe size={14} className="text-slate-400" />
                          {client.website.replace(/^https?:\/\//, '')}
                        </p>
                      )}
                    </div>
                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                      {user?.role === 'super_admin' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLoginAsClient(client);
                          }}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`login-as-client-${client.id}`}
                        >
                          <LogIn size={14} className="mr-1" />
                          View as Client
                        </Button>
                      ) : (
                        <span className="text-xs text-slate-400">Click to view projects</span>
                      )}
                      <FolderKanban size={16} className="text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              )})}
            </div>
          )}
        </div>

        {/* Upcoming Tasks - 1 column */}
        <div>
          <Card className="border-slate-200 shadow-sm sticky top-4" data-testid="tenant-upcoming-tasks">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <ListTodo size={20} className="text-slate-500" />
                Upcoming Tasks
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {upcomingTasks.length > 0 ? (
                <ul className="divide-y divide-slate-100">
                  {upcomingTasks.map((task) => (
                    <li 
                      key={task.id} 
                      className={`px-4 py-3 hover:bg-slate-50 transition-colors border-l-4 ${getPriorityColor(task.priority)}`}
                    >
                      <div className="flex items-start gap-2">
                        <button
                          onClick={() => handleToggleTask(task)}
                          className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center ${
                            task.status === 'completed' 
                              ? 'bg-emerald-500 border-emerald-500' 
                              : 'border-slate-300 hover:border-slate-400'
                          }`}
                          data-testid={`tenant-task-toggle-${task.id}`}
                        >
                          {task.status === 'completed' && (
                            <CheckCircle2 size={10} className="text-white" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p 
                            className={`text-sm cursor-pointer hover:text-blue-600 ${
                              task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-700 font-medium'
                            }`}
                            onClick={() => navigate(`/projects/${task.project_id}`)}
                          >
                            {task.title}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                            {task.project_name && <span>{task.project_name}</span>}
                            {task.due_date && (
                              <span className="flex items-center gap-0.5">
                                <CalendarIcon size={10} />
                                {format(new Date(task.due_date), 'MMM d')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-6 py-8 text-center text-slate-400">
                  <ListTodo size={24} className="mx-auto mb-2 opacity-50" />
                  No upcoming tasks
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Tenant Modal */}
      <Dialog open={isEditTenantOpen} onOpenChange={setIsEditTenantOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Edit Tenant</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateTenant} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Contact Name *</Label>
              <Input
                value={tenantForm.name}
                onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={tenantForm.company_name}
                onChange={(e) => setTenantForm({ ...tenantForm, company_name: e.target.value })}
              />
            </div>
            
            {/* Business Type Selection */}
            <div className="space-y-2">
              <Label>Business Type</Label>
              <div className="grid grid-cols-3 gap-3">
                <div
                  onClick={() => setTenantForm({ ...tenantForm, business_type: 'web_marketing' })}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex items-center gap-2 ${
                    tenantForm.business_type === 'web_marketing'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Briefcase size={18} className={tenantForm.business_type === 'web_marketing' ? 'text-blue-600' : 'text-slate-400'} />
                  <span className={`text-sm font-medium ${tenantForm.business_type === 'web_marketing' ? 'text-blue-700' : 'text-slate-600'}`}>
                    Web & Marketing
                  </span>
                </div>
                <div
                  onClick={() => setTenantForm({ ...tenantForm, business_type: 'construction' })}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex items-center gap-2 ${
                    tenantForm.business_type === 'construction'
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Hammer size={18} className={tenantForm.business_type === 'construction' ? 'text-amber-600' : 'text-slate-400'} />
                  <span className={`text-sm font-medium ${tenantForm.business_type === 'construction' ? 'text-amber-700' : 'text-slate-600'}`}>
                    Construction
                  </span>
                </div>
                <div
                  onClick={() => setTenantForm({ ...tenantForm, business_type: 'dme_medical' })}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex items-center gap-2 ${
                    tenantForm.business_type === 'dme_medical'
                      ? 'border-red-500 bg-red-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Activity size={18} className={tenantForm.business_type === 'dme_medical' ? 'text-red-600' : 'text-slate-400'} />
                  <span className={`text-sm font-medium ${tenantForm.business_type === 'dme_medical' ? 'text-red-700' : 'text-slate-600'}`}>
                    DME Medical
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={tenantForm.contact_email}
                onChange={(e) => setTenantForm({ ...tenantForm, contact_email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={tenantForm.phone}
                onChange={(e) => setTenantForm({ ...tenantForm, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={tenantForm.address}
                onChange={(e) => setTenantForm({ ...tenantForm, address: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Globe size={14} className="text-slate-500" />
                Portal URL Slug
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 whitespace-nowrap">myclientpm.com/</span>
                <Input
                  value={tenantForm.portal_slug}
                  onChange={(e) => setTenantForm({ ...tenantForm, portal_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder="nmnetwork"
                  data-testid="detail-tenant-slug-input"
                />
              </div>
              {tenantForm.portal_slug && (
                <p className="text-xs font-medium text-blue-600 flex items-center gap-1">
                  <Globe size={12} />
                  Portal: https://myclientpm.com/{tenantForm.portal_slug}
                </p>
              )}
              <p className="text-xs text-slate-400">Lowercase letters, numbers and hyphens only</p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Globe size={14} className="text-slate-500" />
                Custom Portal Subdomain
              </Label>
              <Input
                value={tenantForm.custom_domain}
                onChange={(e) => setTenantForm({ ...tenantForm, custom_domain: e.target.value.toLowerCase().trim() })}
                placeholder="e.g. lockheart.myclientpm.com"
                data-testid="detail-tenant-domain-input"
              />
              <p className="text-xs text-slate-500">
                Enter a subdomain under your wildcard domain (e.g. <strong>lockheart.myclientpm.com</strong>).
                Make sure your DNS has a wildcard CNAME: <code>* → myclientpm.com</code>
              </p>
              {tenantForm.custom_domain && (
                <p className="text-xs font-medium text-blue-600 flex items-center gap-1">
                  <Globe size={12} />
                  Portal URL: https://{tenantForm.custom_domain}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditTenantOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">
                Update Tenant
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* SMTP Settings Modal */}
      <Dialog open={isSmtpOpen} onOpenChange={setIsSmtpOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <Mail size={20} />
              Email Settings for {tenant.name}
              {smtpExists && smtpSettings.is_active && (
                <span className="ml-auto flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                  <CheckCircle2 size={12} />
                  Configured
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-500">
              Configure custom SMTP settings for this tenant&apos;s outgoing emails (invoices, notifications, etc.)
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SMTP Host *</Label>
                <div className="relative">
                  <Server size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={smtpSettings.host}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
                    placeholder="smtp.gmail.com"
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Port</Label>
                <Input
                  type="number"
                  value={smtpSettings.port}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, port: parseInt(e.target.value) || 587 })}
                  placeholder="587"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Username *</Label>
                <Input
                  value={smtpSettings.username}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, username: e.target.value })}
                  placeholder="email@domain.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Password {smtpExists ? '(leave blank to keep)' : '*'}</Label>
                <div className="relative">
                  <Input
                    type={showSmtpPassword ? 'text' : 'password'}
                    value={smtpSettings.password}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, password: e.target.value })}
                    placeholder={smtpExists ? '••••••••' : 'Enter password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showSmtpPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Email *</Label>
                <Input
                  type="email"
                  value={smtpSettings.from_email}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, from_email: e.target.value })}
                  placeholder="noreply@domain.com"
                />
              </div>
              <div className="space-y-2">
                <Label>From Name *</Label>
                <Input
                  value={smtpSettings.from_name}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, from_name: e.target.value })}
                  placeholder={tenant.name}
                />
              </div>
            </div>

            <div className="flex items-center gap-6 pt-2">
              <div className="flex items-center gap-3">
                <Switch
                  checked={smtpSettings.use_tls}
                  onCheckedChange={(checked) => setSmtpSettings({ ...smtpSettings, use_tls: checked })}
                />
                <span className="text-sm text-slate-600">Use TLS</span>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={smtpSettings.is_active}
                  onCheckedChange={(checked) => setSmtpSettings({ ...smtpSettings, is_active: checked })}
                />
                <span className="text-sm text-slate-600">Active</span>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsSmtpOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveSmtp}
                disabled={smtpLoading}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {smtpLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {smtpExists ? 'Update Settings' : 'Save Settings'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Storage Settings Dialog */}
      <Dialog open={isStorageOpen} onOpenChange={setIsStorageOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <Cloud className="w-5 h-5" />
              Storage Settings for {tenant.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* iDrive e2 branding and CTA */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <span className="text-lg font-bold text-blue-600">e2</span>
                </div>
                <div>
                  <p className="font-semibold text-slate-900">iDrive e2 Cloud Storage</p>
                  <p className="text-xs text-slate-500">S3-compatible object storage</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('https://console.idrivee2.com/partner/a2gdesigns', '_blank')}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                Get Your Bucket →
              </Button>
            </div>
            
            <p className="text-sm text-slate-500">
              Configure your own iDrive e2 bucket for file storage. If not configured, the global storage will be used.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Endpoint *</Label>
                <Input
                  value={storageSettings.endpoint}
                  onChange={(e) => setStorageSettings({ ...storageSettings, endpoint: e.target.value })}
                  placeholder="s3.us-central-1.idrivee2.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Region *</Label>
                <Input
                  value={storageSettings.region}
                  onChange={(e) => setStorageSettings({ ...storageSettings, region: e.target.value })}
                  placeholder="us-central-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Access Key ID *</Label>
                <Input
                  value={storageSettings.access_key_id}
                  onChange={(e) => setStorageSettings({ ...storageSettings, access_key_id: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Secret Access Key *</Label>
                <div className="relative">
                  <Input
                    type={showStorageSecret ? 'text' : 'password'}
                    value={storageSettings.secret_access_key}
                    onChange={(e) => setStorageSettings({ ...storageSettings, secret_access_key: e.target.value })}
                    placeholder="Enter to update"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowStorageSecret(!showStorageSecret)}
                  >
                    {showStorageSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Bucket Name *</Label>
              <Input
                value={storageSettings.bucket_name}
                onChange={(e) => setStorageSettings({ ...storageSettings, bucket_name: e.target.value })}
                placeholder="your-bucket-name"
              />
            </div>

            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Switch
                checked={storageSettings.is_active}
                onCheckedChange={(checked) => setStorageSettings({ ...storageSettings, is_active: checked })}
              />
              <span className="text-sm text-slate-600">Use custom storage</span>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsStorageOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveStorage}
                disabled={storageLoading}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {storageLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Dialog - Full Browser */}
      <Dialog open={isMediaOpen} onOpenChange={setIsMediaOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <Image className="w-5 h-5" />
              Media Library - {tenant.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-y-auto max-h-[calc(90vh-120px)]">
            <MediaBrowser
              tenantId={tenantId}
              getAuthHeaders={getAuthHeaders}
              canCreateFolders={true}
              canDeleteFolders={user?.role === 'super_admin' || user?.role === 'admin'}
              title="All Files"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantDetail;
